import { useApolloClient, useMutation } from '@apollo/client';
import type { UseCreateOrganization } from '@openx/components-organization/lib/utils/context/OrganizationHookTypes.ts';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  CreateOrganizationDocument,
  type CreateOrganizationMutationVariables,
} from '../../../graphql/mutation/organizations/CreateOrganization.generated.tsx';

export const useCreateOrganization: UseCreateOrganization = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const apolloClient = useApolloClient();

  const [createOrganizationMutation, { loading }] = useMutation(CreateOrganizationDocument, {
    awaitRefetchQueries: true,
    onCompleted: data => {
      apolloClient.cache.evict({
        fieldName: 'account',
        broadcast: false,
      });

      apolloClient.cache.gc();
      enqueueSnackbar(t('Organization created successfully'), { variant: 'success' });

      const navigatePath = data.insert_account_one?.id
        ? `/organizations/${data.insert_account_one?.id}/details`
        : '/organizations';

      navigate(navigatePath);
    },
    onError: ({ graphQLErrors }) => {
      const defaultErrorMessage = t('Failed to create organization');
      const message = graphQLErrors[0]?.message;

      enqueueSnackbar(message ?? defaultErrorMessage, { variant: 'error' });
    },
    refetchQueries: ['GetOrganizationsList'],
  });

  const createOrganization = useCallback(
    async (formData: CreateOrganizationMutationVariables) => {
      await createOrganizationMutation({ variables: formData });
    },
    [createOrganizationMutation]
  );

  return { createOrganization, loading };
};
